import type { ExternalLocationHubConfig } from "@redotech/account-common/address";
import type { ReturnProcessingLevel } from "@redotech/redo-return-settings-primitives-common/return-processing-level";
import type { ReturnsCenterConfig } from "@redotech/redo-return-settings-primitives-common/returns-center-config";
import type { GradeAcceptance, GradeMapping } from "./grade-mapping";
import type { RemitMode } from "./remit-mode";
import type { RedoManagedReturnsSortingMode } from "./returns-sorting-mode";

/**
 * Keep in sync with the generated `ReceivingLabelSettingsRaw` in
 * redo/backend/db/schema/src/types/team/RedoManagedReturnsSettings.schema.d.ts.
 * The `ReceivingLabelField` values are the model-free mirror of
 * `@redotech/redo-inventory-common/receiving-label`; keep them in sync.
 */
export const ReceivingLabelField = {
  returnId: "returnId",
  orderName: "orderName",
} as const;
export type ReceivingLabelField =
  (typeof ReceivingLabelField)[keyof typeof ReceivingLabelField];

export interface ReceivingLabelSettings {
  enabled: boolean;
  encodedField: ReceivingLabelField;
}

export const DEFAULT_RETURNS_PROCESSING_FEE = 1.0;
export const DEFAULT_RETURNS_PROCESSING_COST = 0.0;

/**
 * Keep in sync with the generated `RedoManagedReturnsSettingsRaw` in
 * redo/backend/db/schema/src/types/team/RedoManagedReturnsSettings.schema.d.ts.
 * Fields that are `required` with a Mongoose default in the schema are kept
 * optional here so partial construction at write sites keeps compiling, while
 * carrying the schema's `| null` nullability.
 */
export interface redoManagedReturnsSettings {
  enabled: boolean;
  processingFee: number;
  processingCost: number;
  inventoryPushMinimumQuantity?: number;
  availableReturnsCenters?: ReturnsCenterConfig[];
  returnProcessingLevel?: ReturnProcessingLevel;
  sortingMode?: RedoManagedReturnsSortingMode;
  externalLocationHubMappings?: Record<string, ExternalLocationHubConfig>;
  processingCoverageShare?: number;
  consolidationCoverageShare?: number;
  processingCoverageSharePercent?: number;
  consolidationCoverageSharePercent?: number;
  gradeMappings?: GradeMapping[];
  receivingLabel?: ReceivingLabelSettings;
  remitMode?: RemitMode;
  verificationPassOutcome?: GradeAcceptance;
  verificationFailOutcome?: GradeAcceptance;
}
